// Bangkok-time formatting shared by every follow-up surface (the recall drawer,
// the call log, the patient ติดตาม page). Extracted from RecallPopup.jsx so the
// third caller doesn't become the third copy — `ActBtn`, `callTs` and the
// call_result→Thai map already exist twice each across RecallPopup/CallList.
//
// Everything here treats a value as Bangkok wall-clock. `callTs` in particular
// subtracts 7h because `phone.start_stamp` is a Bangkok-LOCAL epoch, not UTC —
// formatting it directly shows every call 7 hours late.
export const asList = (r) => (Array.isArray(r) ? r : Array.isArray(r?.items) ? r.items : [])
export const bkk = { timeZone: 'Asia/Bangkok' }

// tolerant date → "d/m/yy" (accepts iso string | epoch ms | 10-digit epoch string)
export function fmtDate(v) {
  const iso = toISO(v)
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${Number(d)}/${Number(m)}/${String(y).slice(2)}`
}
export function toISO(v) {
  // `0` has to be excluded too: a phone row with neither start_stamp nor created_at
  // would otherwise render as "1/1/70 07:00" instead of an em dash.
  if (v == null || v === '' || v === 0) return ''
  if (typeof v === 'number') return new Date(v).toLocaleDateString('en-CA', bkk)
  const s = String(v)
  if (/^\d{10,}$/.test(s)) return new Date(Number(s)).toLocaleDateString('en-CA', bkk)
  return s.slice(0, 10)
}
export const MONTHS_TH_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
                                'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

/**
 * tolerant date → "26 ส.ค. 2569" (Thai short month, BE year), Bangkok time.
 *
 * 🚨 THE REASON THIS IS HERE AND NOT INLINE. Both certificate registers rendered
 * the issue date as `String(r.created_at || '').slice(0, 10)` — which is right for
 * an ISO string and catastrophic for a number: `employee_document.created_at` is an
 * epoch in MILLISECONDS, so the first real certificate showed "1787761208", the
 * first ten DIGITS, on both the employee's page and HR's register. Found
 * 2026-08-26 on the first live issue.
 *
 * `toISO` above already handles epoch-ms, numeric strings and ISO strings, and
 * already resolves them in Asia/Bangkok — which matters here because this is the
 * record of when a legal document was issued and must read the same at every
 * branch, whatever a given machine's clock says. Reuse it; do not re-derive.
 */
export function fmtThaiDate(v) {
  const iso = toISO(v)
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  const mi = Number(m) - 1
  if (!MONTHS_TH_SHORT[mi]) return '—'
  return `${Number(d)} ${MONTHS_TH_SHORT[mi]} ${Number(y) + 543}`
}

/**
 * tolerant date → "ส.ค. 2569" (Thai short month + BE year), Bangkok time.
 *
 * The month-granularity sibling of fmtThaiDate, for "which month was this issued"
 * columns and month filters. Same rule, same reason: go through `toISO`, never
 * `String(v).slice(...)` — half the created_at columns in this workspace are epoch
 * MILLISECONDS and slicing one yields the first ten digits of the epoch.
 */
export function fmtThaiMonth(v) {
  const iso = toISO(v)
  if (!iso) return '—'
  const [y, m] = iso.split('-')
  const mi = Number(m) - 1
  if (!MONTHS_TH_SHORT[mi]) return '—'
  return `${MONTHS_TH_SHORT[mi]} ${Number(y) + 543}`
}

// epoch-ms (or numeric string) → "HH:MM" in Bangkok time; '' if no time component
export function fmtTime(v) {
  const n = typeof v === 'number' ? v : (/^\d{10,}$/.test(String(v ?? '')) ? Number(v) : NaN)
  if (!Number.isFinite(n)) return ''
  return new Date(n).toLocaleTimeString('th-TH', { ...bkk, hour: '2-digit', minute: '2-digit' })
}
export function fmtDur(sec) {
  const s = Number(sec) || 0
  if (!s) return '—'
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

export const fmtBaht = (n) => Math.round(Number(n) || 0).toLocaleString('en-US')

export const BKK_OFFSET_MS = 7 * 60 * 60 * 1000
export const callTs = (c) => (c?.start_stamp ? Number(c.start_stamp) - BKK_OFFSET_MS : Number(c?.created_at) || 0)

// Coerce a mixed date value (epoch ms, epoch string, or ISO text) to epoch ms.
export const tsOf = (v) => {
  if (typeof v === 'number') return v
  const s = String(v || '')
  if (/^\d{10,}$/.test(s)) return Number(s)
  return new Date(s).getTime() || 0
}
